import Link from "next/link"
import ArrowRightIcon from "remixicon-react/ArrowRightLineIcon"

import * as Author from "@/domains/authors"
import { useI18n } from "@/lib/i18n"
import { centuriesList } from "@/lib/messages"
import { hiContrastDarkTheme } from "@/stitches"
import {
  AspectRatioImage,
  ColorLayer,
  IconLayer,
  TextLayer,
} from "@/ui/aspect-ratio-image"
import { Box } from "@/ui/box"
import { IconButton } from "@/ui/button"
import { Separator } from "@/ui/separator"
import { Stack } from "@/ui/stack"
import { Text } from "@/ui/text"

type AuthorCardProps = {
  author: Author.AuthorPreview
}

export function AuthorCard({ author }: AuthorCardProps) {
  const { localize } = useI18n()
  const linkProps = { href: Author.hrefOf(author), passHref: true }

  return (
    <Link {...linkProps}>
      <Box as="a" className="group">
        <Box
          as="article"
          css={{ width: "18rem", flexShrink: 0, overflow: "hidden" }}
        >
          <AspectRatioImage
            ratio={3 / 4}
            image={author.images[0]?.file}
            alt={author.name}
          >
            <ColorLayer variant="dimmed" />
            <TextLayer>
              <Stack gap={1} align="end">
                <Stack
                  direction="column"
                  gap={1}
                  css={{ pl: "$6", pb: "$6" }}
                  className={hiContrastDarkTheme}
                >
                  <Text as="h4" size="xl" weight="black">
                    {author.name}
                  </Text>
                  <Stack gap={2} wrap>
                    {author.country && (
                      <Text as="p" variant="label">
                        {localize(author.country.name)}
                      </Text>
                    )}
                    <Separator orientation="vertical" />
                    {author.centuries && (
                      <Text as="p" variant="label">
                        {centuriesList(author.centuries)}
                      </Text>
                    )}
                  </Stack>
                </Stack>
                <IconButton
                  rect
                  variant="solid"
                  color="primary"
                  css={{
                    ml: "auto",
                    flexShrink: 0,
                  }}
                >
                  <ArrowRightIcon size="1.5em" />
                </IconButton>
              </Stack>
            </TextLayer>
            <IconLayer>
              <ArrowRightIcon size="4em" />
            </IconLayer>
          </AspectRatioImage>
        </Box>
      </Box>
    </Link>
  )
}
